import React, { useRef, useEffect, useState } from 'react';
import { useSceneScrollTimings } from '../../hooks/useSceneScrollTimings';
import { GlassCard } from '../shared/GlassCard';
import { Quote } from 'lucide-react';

/**
 * Scene 4 - Future
 * 
 * Shows compounding impact with growth rings and testimonials.
 * Colors: Teal gradient (matches logo branding)
 */

const growthRings = [
  { id: '1yr', year: 1, impact: '$15,000', radius: 100, color: 'rgba(17,197,217,0.2)' },
  { id: '3yr', year: 3, impact: '$45,000', radius: 160, color: 'rgba(17,197,217,0.3)' },
  { id: '5yr', year: 5, impact: '$85,000', radius: 220, color: 'rgba(17,197,217,0.4)' },
];

const testimonials = [
  {
    id: 1,
    quote: "I saved $22,800 my first year — and I sleep better.",
    author: "Sarah C.",
    business: "Marketing Agency Owner",
  },
  {
    id: 2,
    quote: "Five years in, my stress is gone and my team thrives.",
    author: "Marcus R.",
    business: "Manufacturing Business",
  },
];

export const Scene4Future = ({ onSceneChange, onProgressUpdate }) => {
  const sectionRef = useRef(null);
  const [particles, setParticles] = useState([]);

  // Generate floating particles on mount
  useEffect(() => {
    const particleCount = 30;
    const newParticles = Array.from({ length: particleCount }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 20 + 15,
      delay: Math.random() * 5,
      opacity: Math.random() * 0.3 + 0.1,
    }));
    setParticles(newParticles);
  }, []);

  // Track scene entry/exit
  useSceneScrollTimings(sectionRef, {
    start: 'top center',
    end: 'bottom center',
    onEnter: () => {
      if (onSceneChange) onSceneChange(4);
    },
    onUpdate: (progress) => {
      if (onProgressUpdate) onProgressUpdate(75 + progress * 17); // 75-92% of journey
    },
  });

  return (
    <section
      id="future"
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center bg-gradient-future overflow-hidden pt-20"
      data-testid="future-section"
    >
      {/* Noise texture */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIvPjwvc3ZnPg==')] bg-repeat" />

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full bg-teal-300"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              opacity: particle.opacity,
              filter: 'blur(1px)',
              animation: `float ${particle.duration}s ease-in-out infinite`,
              animationDelay: `${particle.delay}s`,
            }}
          />
        ))}
      </div>

      <div className="max-w-6xl mx-auto w-full px-6 sm:px-8 lg:px-12 py-20">
        {/* Header */}
        <div className="text-center mb-16">
          <h2
            className="text-3xl sm:text-4xl lg:text-6xl font-display font-semibold text-white leading-tight mb-4"
            style={{ textShadow: '0 2px 20px rgba(0,0,0,0.6)' }}
            data-testid="future-title"
          >
            The choices you make today
            <br />
            become tomorrow's calm.
          </h2>
          <p
            className="text-lg sm:text-xl text-white/80 font-sans max-w-2xl mx-auto"
            style={{ textShadow: '0 1px 12px rgba(0,0,0,0.5)' }}
            data-testid="future-line"
          >
            Every strategy you apply compounds into freedom.
          </p>
        </div>

        {/* Growth Rings */}
        <div className="relative flex items-center justify-center mb-20 h-[500px]">
          <svg viewBox="0 0 500 500" className="w-full max-w-[500px] h-full">
            {/* Rings */}
            {growthRings.map((ring, index) => (
              <g key={ring.id}>
                {/* Ring circle */}
                <circle
                  cx="250"
                  cy="250"
                  r={ring.radius}
                  fill="none"
                  stroke={ring.color}
                  strokeWidth="2"
                  className="animate-pulse-soft"
                  style={{ animationDelay: `${index * 0.3}s` }}
                  data-testid={`growth-ring-${ring.id}`}
                />
                
                {/* Ring glow */}
                <circle
                  cx="250"
                  cy="250"
                  r={ring.radius}
                  fill="none"
                  stroke={ring.color}
                  strokeWidth="8"
                  opacity="0.3"
                  filter="blur(8px)"
                  className="animate-pulse-soft"
                  style={{ animationDelay: `${index * 0.3}s` }}
                />

                {/* Ring label */}
                <text
                  x="250"
                  y={250 - ring.radius - 15}
                  textAnchor="middle"
                  className="fill-white text-base font-sans font-semibold"
                  style={{ textShadow: '0 2px 12px rgba(0,0,0,0.8)' }}
                  data-testid={`ring-label-${ring.id}`}
                >
                  {ring.year} {ring.year === 1 ? 'Year' : 'Years'}
                </text>
                <text
                  x="250"
                  y={250 - ring.radius + 5}
                  textAnchor="middle"
                  className="fill-teal-300 text-2xl font-mono font-bold"
                  style={{ textShadow: '0 0 20px rgba(17,197,217,0.5)' }}
                >
                  {ring.impact}
                </text>
              </g>
            ))}

            {/* Center icon */}
            <circle cx="250" cy="250" r="30" fill="rgba(17,197,217,0.2)" />
            <circle cx="250" cy="250" r="20" fill="rgba(17,197,217,0.4)" />
            <circle cx="250" cy="250" r="10" fill="#11C5D9" className="animate-pulse" />
          </svg>
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {testimonials.map((testimonial) => (
            <GlassCard
              key={testimonial.id}
              className="p-8"
              testId={`testimonial-card-${testimonial.id}`}
            >
              <Quote className="w-8 h-8 text-teal-300 mb-4" />
              <p
                className="text-lg font-sans text-white/90 mb-6 italic"
                style={{ textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}
                data-testid="testimonial-quote"
              >
                "{testimonial.quote}"
              </p>
              <div>
                <p
                  className="text-base font-sans font-semibold text-white"
                  data-testid="testimonial-author"
                >
                  {testimonial.author}
                </p>
                <p className="text-sm font-sans text-white/60">
                  {testimonial.business}
                </p>
              </div>
            </GlassCard>
          ))}
        </div>

        {/* Subtext */}
        <p
          className="text-center text-sm sm:text-base text-white/60 font-sans"
          style={{ textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}
          data-testid="future-subtext"
        >
          Results verified by independent CPAs.
        </p>
      </div>
    </section>
  );
};